import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, HelpCircle, RefreshCw } from 'lucide-react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { fetchBonusAuditRecords, type BonusAuditRecord } from '../../features/wallet/api';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { localTimeWithTz } from '../../shared/lib/datetime';
import { fmtMoney } from '../../shared/lib/format';

// `legacy_balance` is a migration-only compatibility lot for the player's
// pre-framework balance. It remains eligible for withdrawal, but it is not a
// deposit or a bonus grant and must not look like a player audit record.
/**
 * `sourceLabel` is a machine identifier — the API sends `deposit_coupon_bonus`,
 * `deposit`, `legacy_balance`, and elsewhere Chinese operator labels. Rendering
 * it raw put `deposit_coupon_bonus` in front of Brazilian players. Unknown
 * sources fall back to the record's kind, which is always meaningful, never to
 * the identifier.
 */
function auditSourceText(record: BonusAuditRecord, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const source = String(record.sourceLabel || '').trim().toLowerCase();
  if (source === 'deposit') return t('auditSourceDeposit');
  if (source === 'deposit_coupon_bonus') return t('auditSourceDepositCouponBonus');
  return record.kind === 'bonus' ? t('auditSourceBonus') : t('auditSourcePrincipal');
}

function isVisibleAuditRecord(record: BonusAuditRecord) {
  if (String(record.sourceLabel || '').trim().toLowerCase() === 'legacy_balance') return false;
  return record.kind === 'bonus'
    ? Number(record.bonusRequired || 0) > 0
    : Number(record.principalRequired || 0) > 0;
}

export function AuditProgressPage() {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const setRoute = useAppStore((state) => state.setRoute);
  // Back returns to where the page was opened from — the Wallet's money card
  // and its withdrawal lock both lead here, not just the Me menu. Consumed on
  // use so a later direct hit (deep link, reload) still falls back to Me.
  const auditReturn = useAppStore((state) => state.auditReturn);
  const setAuditReturn = useAppStore((state) => state.setAuditReturn);
  const goBack = () => {
    const target = auditReturn || 'me';
    setAuditReturn(null);
    setRoute(target);
  };
  const t = useI18nStore((state) => state.t);
  const query = useQuery({
    queryKey: ['me-bonus-audit-records', token],
    queryFn: () => fetchBonusAuditRecords(token || ''),
    enabled: !!token && !guestMode,
    retry: false,
  });
  const records = (query.data?.items || []).filter(isVisibleAuditRecord);

  return (
    <section className="tab-page audit-progress-page">
      <header className="subroute-header">
        <button className="icon-button" type="button" onClick={goBack} aria-label={t('back')}>
          <ArrowLeft size={19} aria-hidden="true" />
        </button>
        <div>
          <h1>{t('auditProgressTitle')}</h1>
        </div>
      </header>
      <div className="audit-progress-list">
        {query.isLoading ? <AuditProgressState icon={RefreshCw} label={t('loading')} /> : null}
        {query.isError ? <AuditProgressState icon={HelpCircle} label={t('auditLoadFailed')} actionLabel={t('retry')} onAction={() => query.refetch()} /> : null}
        {!query.isLoading && !query.isError ? records.map((record) => <AuditProgressCard key={record.id} record={record} />) : null}
      </div>
    </section>
  );
}

function AuditProgressState({ icon: Icon, label, actionLabel, onAction }: { icon: typeof RefreshCw; label: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="sheet-state">
      <Icon size={22} aria-hidden="true" />
      <span>{label}</span>
      {actionLabel && onAction ? <button className="sheet-action" type="button" onClick={onAction}>{actionLabel}</button> : null}
    </div>
  );
}

function AuditProgressCard({ record }: { record: BonusAuditRecord }) {
  const t = useI18nStore((state) => state.t);
  const bonus = record.kind === 'bonus';
  const abandoned = record.status === 'abandoned';
  const exhausted = record.terminationReason === 'balance_exhausted';
  const endLabel = exhausted ? 'auditCashExhausted' : 'auditEnded';
  const endNote = exhausted ? 'auditCashExhaustedNote' : 'auditEndedNote';
  const frozen = record.withdrawalBonusStatus === 'held';
  const forfeited = record.withdrawalBonusStatus === 'forfeited';
  const required = Number(bonus ? record.bonusRequired : record.principalRequired || 0);
  const remaining = Math.max(0, Number(bonus ? record.bonusRemaining : record.principalRemaining || 0));
  const consumed = Math.max(0, required - remaining);
  const progress = required > 0 ? Math.min(100, (consumed / required) * 100) : 100;
  const completed = record.status === 'completed' || remaining <= 0;
  const granted = Number(record.bonusGranted || 0);
  const multiplier = bonus && granted > 0 ? required / granted : 0;
  const multiplierLabel = multiplier % 1 === 0 ? String(multiplier) : multiplier.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return (
    <article className={`audit-progress-card${abandoned ? ' abandoned' : completed ? ' completed' : ''}`}>
      <div className="audit-progress-heading">
        <strong>{bonus ? t('auditBonus') : t('auditPrincipal')}</strong>
        <b className={abandoned ? 'abandoned' : completed ? 'completed' : ''}>{frozen ? t('auditWithdrawalFrozen') : forfeited ? t('auditWithdrawalForfeited') : abandoned ? t(endLabel) : completed ? t('auditCompleted') : t('auditRemaining')}</b>
      </div>
      <div className="audit-progress-source">
        <span>{auditSourceText(record, t)}</span>
        {bonus && multiplier > 0 ? <span className="audit-progress-multiplier">{t('rewardRollover').replace('%x%', multiplierLabel)}</span> : null}
      </div>
      {abandoned ? (
        <div className="audit-progress-values"><b>{t(forfeited ? 'auditWithdrawalForfeited' : endLabel)}</b><small>{t(forfeited ? 'auditWithdrawalForfeitedNote' : endNote)}</small></div>
      ) : (
        <>
          <div className="audit-progress-values"><b>{fmtMoney(consumed)} / {fmtMoney(required)}</b><small>{completed ? t('auditCompleted') : `${t('auditRemaining')} ${fmtMoney(remaining)}`}</small></div>
          <div className="audit-progress-track" aria-label={`${Math.round(progress)}%`}><i style={{ width: `${progress}%` }} /></div>
        </>
      )}
      {frozen ? <p className="withdraw-warning">{t('pixWithdrawBonusLifecycle')}</p> : null}
      <small>{localTimeWithTz(Number(record.enrolledAt || 0))}</small>
    </article>
  );
}
